import { Promise } from 'bluebird';
import { useState, useCallback, useContext, useMemo, useEffect, useRef } from 'react';
import _ from 'underscore';

import {
  DataServiceContext,
  InstrumentationServiceContext,
  useIsSmartPageImageTextSuggestionEnabled,
  useIsSmartPageM365StockImageEnabled,
  useIsSmartPageM365StockImageGalleryEnabled,
  useIsM365Premium,
} from '@bingads-webui-campaign-react/smart-page-context';

const DEBOUNCE_WAIT_IN_MS = 600;

export const ContentSuggestionOption = {
  None: '0',
  ImageOnly: '1',
  TextOnly: '2',
  Both: '3',
};

export const ContentSuggestionSource = {
  None: '0',
  TemplateAdsOnly: '1',
  M365: '2',
  Both: '3',
};

/**
 * Gets a list of image or text suggestion
 * @param {string[]} businessName - business name
 * @param {string[]} keywords - search keywords
 * @param {string} resultOption - the control option of whether return image or text
 * resultOption : 0 => none, 1 => image only, 2 => text only, 3 => both
 * @param {boolean} initWithLoading - make the initial state loading,
 *  so we don't need to wait for the first debounce get trigger
 * @returns {Object} - content suggestions.
 */
export function useContentSuggestions(businessName = '', keywords = [], resultOption = ContentSuggestionOption.None, initWithLoading = false) {
  const [suggestions, setSuggestions] = useState({ imageSuggestions: null, textSuggestion: null });
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(initWithLoading);
  const { imageSuggestions, textSuggestion } = suggestions;

  const previousSearchOptions = useRef(null);

  const { smartPageService } = useContext(DataServiceContext);
  const logger = useContext(InstrumentationServiceContext);

  const isSmartPageImageTextSuggestionEnabled = useIsSmartPageImageTextSuggestionEnabled();
  const isSmartPageM365StockImageEnabled = useIsSmartPageM365StockImageEnabled();
  const isSmartPageM365StockImageGalleryEnabled = useIsSmartPageM365StockImageGalleryEnabled();
  const isM365Premium = useIsM365Premium();

  const imageSource = isM365Premium ?
    ContentSuggestionSource.Both : ContentSuggestionSource.TemplateAdsOnly;

  const getContentSuggestions = useCallback((cbBusinessName, cbKeywords, cbResultOption) => {
    if (!isSmartPageImageTextSuggestionEnabled
      && !isSmartPageM365StockImageEnabled
      && !isSmartPageM365StockImageGalleryEnabled) {
      return Promise.resolve();
    }

    const finalBusinessName = cbBusinessName || businessName;
    const finalKeywords = cbKeywords || keywords;
    const finalResultOption = cbResultOption || resultOption;

    previousSearchOptions.current = {
      businessName: finalBusinessName,
      keywords: finalKeywords,
      option: finalResultOption,
    };

    if (
      (finalBusinessName || finalKeywords.length > 0) &&
      finalResultOption !== ContentSuggestionOption.None
    ) {
      setIsLoading(true);
      return smartPageService.getContentSuggestions(
        finalBusinessName,
        finalKeywords,
        finalResultOption,
        imageSource
      )
        .then((res) => {
          const newTextSuggestion = res.Text || '';
          const newImageSuggestions = res.Images || [];

          setSuggestions({
            imageSuggestions: newImageSuggestions,
            textSuggestion: newTextSuggestion,
          });

          logger.logTrace(`Successfully get suggestions for business name: ${finalBusinessName}, keywords: ${finalKeywords}, option: ${finalResultOption}, source: ${imageSource}, text suggestion count: ${newTextSuggestion ? 1 : 0}, image suggestion count: ${newImageSuggestions.length}`);
          return res;
        })
        .catch((err) => {
          setError(err);
          logger.logError('Error while getting suggestions', err);

          throw err;
        })
        .finally(() => {
          setIsLoading(false);
        });
    }

    setSuggestions({
      imageSuggestions: null,
      textSuggestion: null,
    });

    return Promise.resolve();
  }, [
    keywords,
    resultOption,
    smartPageService,
    logger,
    isSmartPageImageTextSuggestionEnabled,
    isSmartPageM365StockImageEnabled,
    businessName,
    isSmartPageM365StockImageGalleryEnabled,
    imageSource,
  ]);

  const debouncedGetContentSuggestions = useMemo(
    () => _.debounce(getContentSuggestions, DEBOUNCE_WAIT_IN_MS),
    [getContentSuggestions]
  );

  useEffect(() => {
    const shouldGetSuggestion = !previousSearchOptions.current
      || previousSearchOptions.current.businessName !== businessName
      || previousSearchOptions.current.keywords.toString() !== keywords.toString()
      || previousSearchOptions.current.option !== resultOption;

    if (shouldGetSuggestion) {
      if (
        (_.isEmpty(businessName) && _.isEmpty(keywords))
        || resultOption === ContentSuggestionOption.None
      ) {
        setSuggestions({
          imageSuggestions: null,
          textSuggestion: null,
        });

        previousSearchOptions.current = {
          businessName,
          keywords,
          option: resultOption,
        };
      } else {
        debouncedGetContentSuggestions();
      }
    }

    return () => debouncedGetContentSuggestions.cancel();
  }, [
    businessName,
    debouncedGetContentSuggestions,
    keywords,
    resultOption,
  ]);

  return {
    imageSuggestions,
    textSuggestion,
    error,
    isLoading,
    getContentSuggestions,
  };
}
